import React from "react";

import {
  IconDefinition,
  faCheckCircle,
  faExclamationCircle,
  faInfoCircle,
  faSyncAlt,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDispatch } from "react-redux";
import styled from "styled-components/macro";

import { appUpdateComplete } from "actions";

import Clickable from "components/Button/Clickable";

export type NotificationType =
  | "error"
  | "update"
  | "optionalUpdate"
  | "warning"
  | "syncing"
  | "success"
  | "info";

interface NotificationProps {
  type: NotificationType;
  message: React.ReactNode;
  dismiss: () => void;
  dismissMessage?: string | null;
  dismissAction?: (() => void) | null;
  progress?: number | null;
  onCancel?: (() => void) | null;
}

interface ColorProps {
  $color: string;
}

const Box = styled.div<ColorProps & { $backgroundColor: string }>`
  display: flex;
  position: relative;
  background-color: ${({ theme, $backgroundColor }) =>
    theme.colors[$backgroundColor]};
  color: ${({ theme }) => theme.colors.gray40};
  line-height: 21px;
  font-size: 14px;
  font-family: inherit;
  max-height: 96px;
  border-style: solid;
  border-width: 0 0 0 6px;
  border-color: ${({ theme, $color }) => theme.colors[$color]};
  box-shadow: 0 1px 4px 0 #666666;
`;

const IconCell = styled.div<ColorProps>`
  color: ${({ theme, $color }) => theme.colors[$color]};
  display: flex;
  align-items: center;
  max-height: 96px;
  width: 48px;
  border-style: none;
  text-align: center;
  padding: 18px;
`;

const TextCell = styled.div`
  display: flex;
  flex-grow: 1;
  align-items: center;
  max-height: 96px;
  border-style: none;
  text-align: left;
  padding: 0 15px 0 5px;
`;

const ProgressText = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: ${({ theme }) => theme.space[1]}px 0;
`;

const ProgressTrack = styled.div`
  height: ${({ theme }) => theme.space[1]}px;
  margin-top: ${({ theme }) => theme.space[1]}px;
  border-radius: ${({ theme }) => theme.radii[2]}px;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.gray95};
`;

const ProgressFill = styled.div<{ $progress: number }>`
  height: 100%;
  width: ${({ $progress }) => Math.min(Math.max($progress, 0), 100)}%;
  transition: ${({ theme }) => theme.transitions[0]};
  background-color: ${({ theme }) => theme.colors.primary};
`;

const DismissCell = styled(Clickable)`
  display: flex;
  align-items: center;
  text-align: middle;
  max-height: 96px;
  width: 48px;
  border-style: solid;
  border-color: #dbd9d8;
  border-width: 0 0 0 0.5px;
  padding: 18px;
`;

const RefreshText = styled(Clickable)`
  display: flex;
  flex-grow: 1;
  align-items: right;
  max-height: 96px;
  border-style: none;
  text-align: right;
  margin-top: auto;
  margin-bottom: auto;
  margin-right: 16px;
  text-decoration: underline;
`;

const CancelText = styled(RefreshText)`
  flex-grow: 0;
  margin-left: ${({ theme }) => theme.space[1]}px;
  white-space: nowrap;
`;

interface TypeConfig {
  icon: IconDefinition;
  color?: string;
  backgroundColor?: string;
  showCloseButton?: boolean;
}

const typeConfigs: Record<NotificationType, TypeConfig> = {
  error: { icon: faExclamationCircle },
  update: {
    icon: faExclamationCircle,
    backgroundColor: "warning",
    showCloseButton: false,
  },
  optionalUpdate: { icon: faInfoCircle },
  warning: { icon: faExclamationCircle },
  syncing: { icon: faSyncAlt },
  success: { icon: faCheckCircle },
  info: { icon: faInfoCircle, color: "gray40" },
};

const Notification = ({
  type,
  message,
  dismiss,
  dismissMessage = null,
  dismissAction = null,
  progress = null,
  onCancel = null,
}: NotificationProps): React.JSX.Element => {
  const dispatch = useDispatch();
  const {
    icon,
    color = type,
    backgroundColor = "white",
    showCloseButton = true,
  } = typeConfigs[type] || typeConfigs.info;

  const refreshApp = () => {
    dispatch(appUpdateComplete());
    window.location.href = `/?${new Date().getTime()}`;
  };
  const isUpdate = type === "update" || type === "optionalUpdate";
  const action = isUpdate ? refreshApp : dismissAction;
  const actionLabel = isUpdate ? "REFRESH" : dismissMessage;
  const showAction = isUpdate || (type === "error" && action && actionLabel);
  const boxClickAction = type === "optionalUpdate" ? dismiss : dismissAction;

  return (
    <Box
      $color={color}
      $backgroundColor={backgroundColor}
      onClick={boxClickAction}
    >
      <IconCell $color={color}>
        <FontAwesomeIcon
          icon={icon}
          style={{ fontSize: 18 }}
          spin={type === "syncing"}
          data-tour={`toast.${type}`}
        />
      </IconCell>
      <TextCell>
        {progress === null ? (
          message || "Error"
        ) : (
          <ProgressText>
            {message}
            <ProgressTrack>
              <ProgressFill $progress={progress} />
            </ProgressTrack>
          </ProgressText>
        )}
      </TextCell>
      {showAction && (
        <RefreshText
          onClick={() => {
            action();
            dismiss();
          }}
        >
          {actionLabel}
        </RefreshText>
      )}
      {onCancel && (
        <CancelText
          data-tour="cancel"
          onClick={() => {
            onCancel();
            dismiss();
          }}
        >
          Cancel
        </CancelText>
      )}
      {showCloseButton && progress === null && (
        <DismissCell onClick={dismiss}>
          <FontAwesomeIcon icon={faTimes} style={{ fontSize: 20 }} />
        </DismissCell>
      )}
    </Box>
  );
};

export default Notification;
